import { useState } from 'react';
import { useNavigate } from 'react-router';
import { KeyRound, Wallet } from 'lucide-react';
import { authApi } from '../api/endpoints';
import { errorMessage } from '../api/client';
import { Button, Callout, Segmented } from '../ui';

/**
 * The sign-in buttons. The wallet path leaves the app; the development key
 * (dev builds only) signs locally and lands on the same callback page, so
 * both exercise the real verification path.
 */
export function SignInActions({ label = 'Sign in with SmartLedger Wallet' }: { label?: string }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<'wallet' | 'dev' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [identity, setIdentity] = useState<'A' | 'B'>(() => {
    if (!import.meta.env.DEV) return 'A';
    try {
      return localStorage.getItem('ch:dev-identity') === 'B' ? 'B' : 'A';
    } catch {
      return 'A';
    }
  });

  async function wallet() {
    setBusy('wallet');
    setError(null);
    try {
      const { loginUrl } = await authApi.challenge();
      window.location.assign(loginUrl);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(null);
    }
  }

  async function devKey() {
    if (!import.meta.env.DEV) return;
    setBusy('dev');
    setError(null);
    try {
      const dev = await import('../dev/devWallet');
      dev.setDevIdentity(identity);
      const { challenge } = await authApi.challenge();
      navigate(await dev.devSignIn(challenge, identity));
    } catch (err) {
      setError(errorMessage(err));
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Button variant="primary" size="lg" block icon={<Wallet />} loading={busy === 'wallet'} disabled={busy !== null} onClick={wallet}>
        {label}
      </Button>
      <p className="subtle" style={{ fontSize: 'var(--text-sm)', textAlign: 'center' }}>
        No password. Your wallet signs a one-time challenge for this site.
      </p>
      {error && (
        <Callout tone="crit" role="alert" title="Couldn’t start sign-in">
          {error}
        </Callout>
      )}
      {import.meta.env.DEV && (
        <div className="dev-signin">
          <div className="ui-divider-label">Development only</div>
          <div className="dev-signin__row">
            <Segmented
              label="Development identity"
              value={identity}
              onChange={setIdentity}
              options={[
                { value: 'A', label: 'Identity A' },
                { value: 'B', label: 'Identity B' },
              ]}
            />
            <Button variant="secondary" icon={<KeyRound />} loading={busy === 'dev'} disabled={busy !== null} onClick={devKey}>
              Use a development key
            </Button>
          </div>
          <p className="subtle" style={{ fontSize: 'var(--text-xs)' }}>
            A random key kept in this browser signs the challenge. Switch identities to test invites and roles.
          </p>
        </div>
      )}
    </div>
  );
}
